import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, KeyRound, QrCode, Printer as PrinterIcon, Copy, RefreshCw, Trash2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const ROLES = [
    { id: "admin", label: "Admin / Cassa" },
    { id: "cashier", label: "Cassa" },
    { id: "waiter", label: "Cameriere" },
    { id: "kitchen", label: "Cucina" },
    { id: "bar", label: "Bar" },
    { id: "pizza", label: "Pizzeria" },
];

const Utenti = () => {
    const [users, setUsers] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ email: "", password: "", name: "", role: "waiter" });
    const [resetFor, setResetFor] = useState(null);
    const [newPass, setNewPass] = useState("");
    const [qrFor, setQrFor] = useState(null); // {user, token}

    const load = async () => {
        const { data } = await api.get("/users");
        setUsers(data);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            await api.post("/users", form);
            toast.success("Utente creato");
            setShow(false);
            setForm({ email: "", password: "", name: "", role: "waiter" });
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const toggleActive = async (u) => {
        await api.patch(`/users/${u.id}`, { active: !u.active });
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare l'utente?")) return;
        await api.delete(`/users/${id}`);
        load();
    };

    const resetPassword = async () => {
        try {
            await api.patch(`/users/${resetFor.id}`, { password: newPass });
            toast.success("Password aggiornata");
            setResetFor(null); setNewPass("");
        } catch (e) { toast.error(formatError(e)); }
    };

    const generateQr = async (u) => {
        try {
            const { data } = await api.post(`/users/${u.id}/qr-login`);
            setQrFor({ user: u, token: data.token });
            toast.success(`QR generato per ${u.name}`);
        } catch (e) { toast.error(formatError(e)); }
    };

    const revokeQr = async (u) => {
        if (!window.confirm(`Revocare il QR di ${u.name}? Il vecchio QR non funzionerà più.`)) return;
        try {
            await api.delete(`/users/${u.id}/qr-login`);
            toast.success("QR revocato");
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="utenti-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Personale</div>
                    <h1 className="font-display text-4xl font-bold">Utenti & Permessi</h1>
                    <p className="text-elegant text-sm mt-2">
                        Genera un QR personale per l'accesso rapido di camerieri e cucina.
                    </p>
                </div>
                <button onClick={() => setShow(true)} data-testid="add-user-button" className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Nuovo Utente
                </button>
            </div>

            <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-surface-elevated">
                        <tr className="text-left text-elegant text-xs uppercase tracking-wider">
                            <th className="px-4 py-3">Nome</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Ruolo</th>
                            <th className="px-4 py-3">Stato</th>
                            <th className="px-4 py-3">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t border-[#1f2c44]" data-testid={`user-${u.id}`}>
                                <td className="px-4 py-3 font-medium">{u.name}</td>
                                <td className="px-4 py-3 text-elegant">{u.email}</td>
                                <td className="px-4 py-3"><span className="text-xs uppercase tracking-wider text-brand">{u.role}</span></td>
                                <td className="px-4 py-3">
                                    <button onClick={() => toggleActive(u)} className={`text-xs px-3 py-1 rounded-full ${u.active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}`}>
                                        {u.active ? "Attivo" : "Disattivo"}
                                    </button>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                        <button onClick={() => generateQr(u)}
                                            className="text-elegant hover:text-brand"
                                            title="Genera/visualizza QR di accesso"
                                            data-testid={`qr-gen-${u.id}`}>
                                            <QrCode className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => revokeQr(u)}
                                            className="text-elegant hover:text-amber-400"
                                            title="Revoca QR (rigenerare con il pulsante QR)"
                                            data-testid={`qr-revoke-${u.id}`}>
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setResetFor(u)} className="text-elegant hover:text-brand" title="Reset password" data-testid={`reset-pw-${u.id}`}>
                                            <KeyRound className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => remove(u.id)} className="text-red-400 hover:text-red-300" title="Elimina utente"><X className="w-4 h-4" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                        <h2 className="font-display text-2xl font-bold mb-4">Nuovo Utente</h2>
                        <div className="space-y-3">
                            <input className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
                            <input type="email" className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
                            <input type="password" className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                            <select className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                            </select>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setShow(false)} className="flex-1 h-12 border border-[#1f2c44] rounded-md">Annulla</button>
                            <button onClick={create} data-testid="save-user-button" className="flex-1 h-12 bg-brand text-deep font-bold rounded-md">Crea</button>
                        </div>
                    </div>
                </div>
            )}

            {resetFor && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                        <h2 className="font-display text-xl font-bold mb-2">Reset Password</h2>
                        <div className="text-elegant text-sm mb-4">{resetFor.email}</div>
                        <input type="password" placeholder="Nuova password" value={newPass} onChange={(e) => setNewPass(e.target.value)}
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" data-testid="new-password-input" />
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setResetFor(null)} className="flex-1 h-12 border border-[#1f2c44] rounded-md">Annulla</button>
                            <button onClick={resetPassword} className="flex-1 h-12 bg-brand text-deep font-bold rounded-md">Aggiorna</button>
                        </div>
                    </div>
                </div>
            )}

            {qrFor && (
                <QrModal qrFor={qrFor} onClose={() => setQrFor(null)} />
            )}
        </div>
    );
};

const QrModal = ({ qrFor, onClose }) => {
    const printRef = useRef(null);
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(qrFor.token);
            setCopied(true);
            toast.success("Token copiato");
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            toast.error("Impossibile copiare");
        }
    };

    const print = () => {
        const svg = printRef.current?.querySelector("svg");
        if (!svg) return;
        const svgString = new XMLSerializer().serializeToString(svg);
        const w = window.open("", "_blank", "width=600,height=800");
        if (!w) { toast.error("Popup bloccato dal browser"); return; }
        w.document.write(`
            <html>
                <head>
                    <title>QR - ${qrFor.user.name}</title>
                    <style>
                        body { font-family: system-ui, sans-serif; padding: 40px; text-align: center; }
                        h1 { font-size: 28px; margin-bottom: 8px; }
                        .role { color: #14E5BC; text-transform: uppercase; letter-spacing: 0.2em; font-size: 12px; margin-bottom: 32px; }
                        .qr { padding: 20px; background: white; display: inline-block; border: 1px solid #ddd; border-radius: 12px; }
                        .footer { margin-top: 32px; font-size: 11px; color: #666; max-width: 400px; margin-left:auto; margin-right:auto; line-height: 1.5; }
                        .brand { margin-top: 48px; font-size: 14px; color: #999; letter-spacing: 0.2em; text-transform: uppercase; }
                    </style>
                </head>
                <body>
                    <h1>${qrFor.user.name}</h1>
                    <div class="role">${qrFor.user.role}</div>
                    <div class="qr">${svgString}</div>
                    <div class="footer">
                        Scansiona questo QR dalla pagina di login Smart Table per accedere immediatamente al tuo terminale.
                        Conserva il foglio in luogo sicuro: chiunque ha accesso al QR può effettuare il login.
                    </div>
                    <div class="brand">Smart Table 360 by renova360</div>
                    <script>setTimeout(() => { window.print(); window.close(); }, 300);</script>
                </body>
            </html>
        `);
        w.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="qr-modal">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="font-display text-2xl font-bold">QR di accesso</h2>
                        <div className="text-elegant text-sm">{qrFor.user.name} · {qrFor.user.role}</div>
                    </div>
                    <button onClick={onClose}><X /></button>
                </div>

                <div ref={printRef} className="flex justify-center mb-4 p-4 bg-white rounded-lg">
                    <QRCodeSVG value={qrFor.token} size={240} level="M" includeMargin={true} />
                </div>

                <div className="bg-surface-elevated border border-[#1f2c44] rounded-md p-3 mb-4">
                    <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">Token (copiare per fallback manuale)</div>
                    <div className="text-[10px] text-warm break-all font-mono">{qrFor.token.slice(0, 80)}…</div>
                </div>

                <div className="flex gap-2">
                    <button onClick={copy} data-testid="qr-copy-button"
                        className="flex-1 h-12 border border-[#1f2c44] hover:border-brand rounded-md text-sm flex items-center justify-center gap-2">
                        <Copy className="w-4 h-4" /> {copied ? "Copiato" : "Copia token"}
                    </button>
                    <button onClick={print} data-testid="qr-print-button"
                        className="flex-1 h-12 bg-brand text-deep font-bold rounded-md text-sm flex items-center justify-center gap-2">
                        <PrinterIcon className="w-4 h-4" /> Stampa
                    </button>
                </div>

                <div className="text-[11px] text-elegant mt-4 leading-relaxed">
                    Il QR resta valido finché non viene revocato (10 anni di durata massima).
                    Per invalidarlo usa il pulsante <span className="text-amber-400">↻ Revoca</span> nella tabella utenti.
                </div>
            </div>
        </div>
    );
};

export default Utenti;
